import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", pin: "", role: "Staff", phone: "", hourly_wage: 0 };

export default function EmployeesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setList(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const startEdit = (emp) => {
    setEditing(emp);
    setForm({
      name: emp.name,
      pin: emp.pin,
      role: emp.role,
      phone: emp.phone || "",
      hourly_wage: emp.hourly_wage || 0,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        hourly_wage: parseFloat(form.hourly_wage) || 0,
      };
      if (editing) {
        await api.put(`/employees/${editing.id}`, body);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", body);
        toast.success("Employee added");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/employees/${toDelete.id}`);
      toast.success("Employee removed");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="employees-page">
      <div className="flex items-end justify-between fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Team</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Employees</h1>
          <p className="mt-3 text-muted-foreground">Add staff, set PINs, adjust wages.</p>
        </div>
        <Button
          onClick={startNew}
          data-testid="add-employee-btn"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> Add employee
        </Button>
      </div>

      <div className="mt-10 bg-white border border-border fade-up">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No employees yet.</p>
            <p className="text-muted-foreground mt-2">Add your first staff member to begin.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[680px]" data-testid="employees-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-6 py-4 font-normal">Name</th>
                <th className="px-6 py-4 font-normal">Role</th>
                <th className="px-6 py-4 font-normal">PIN</th>
                <th className="px-6 py-4 font-normal">Phone</th>
                <th className="px-6 py-4 font-normal text-right">Wage / hr</th>
                <th className="px-6 py-4 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.id} className="border-t border-border" data-testid={`employee-row-${e.id}`}>
                  <td className="px-6 py-4 font-medium">{e.name}</td>
                  <td className="px-6 py-4 text-muted-foreground">{e.role}</td>
                  <td className="px-6 py-4 font-mono tracking-widest">{e.pin}</td>
                  <td className="px-6 py-4 text-muted-foreground">{e.phone || "—"}</td>
                  <td className="px-6 py-4 text-right font-serif">₹{e.hourly_wage?.toFixed(2)}</td>
                  <td className="px-6 py-4 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(e)}
                      data-testid={`edit-employee-${e.id}`}
                    >
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setToDelete(e)}
                      data-testid={`delete-employee-${e.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {editing ? "Edit employee" : "Add employee"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-testid="emp-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pin">PIN (4–6 digits)</Label>
                <Input
                  id="pin"
                  data-testid="emp-pin"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{4,6}"
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  data-testid="emp-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="emp-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="wage">Hourly wage</Label>
                <Input
                  id="wage"
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="emp-wage"
                  value={form.hourly_wage}
                  onChange={(e) => setForm({ ...form, hourly_wage: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="save-employee-btn"
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {busy ? "Saving…" : editing ? "Save changes" : "Add employee"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif tracking-tight">Remove {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Their attendance history will remain, but the employee will no longer be able to check in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
